import { zodResolver } from '@hookform/resolvers/zod';
import { orderStatusOptions } from 'apps/investment/constants/order';
import { Button } from 'common/ui/button';
import { Input } from 'common/ui/input';
import { Label } from 'common/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'common/ui/select';
import { Controller, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { sanitizeFormData } from 'utils/filter-form';
import { z } from 'zod';

const filterSchema = z.object({
  status: z.string().optional(),
  order_side: z.string().optional(),
  source: z.string().optional(),
  search: z.string().optional(),
});

type FilterFormValues = {
  status?: string,
  order_side?: string,
  source?: string,
  search?: string,
};

export const FilterForm = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    status,
    order_side,
    search,
    source,
    ...queryParams
  } = Object.fromEntries(searchParams.entries());

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(filterSchema),
    defaultValues: {
      status: status || 'all',
      order_side: order_side || 'all',
      source: source || 'all',
      search: search || '',
    },
  });

  const onSubmit = (data: FilterFormValues) => {
    const sanitizedData = sanitizeFormData(data);
    setSearchParams({
      ...queryParams,
      ...sanitizedData,
    });
  };

  const handleReset = () => {
    reset({
      status: 'all',
      order_side: 'all',
      source: 'all',
      search: '',
    });
    setSearchParams(queryParams);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-wrap gap-2">
        <div className="flex flex-col gap-1 min-w-[200px]">
          <Label htmlFor="search">Search</Label>
          <Controller
            name="search"
            control={control}
            render={({ field }) => (
              <Input
                type="text"
                id="search"
                placeholder="Search..."
                value={field.value || ''}
                onChange={e => field.onChange(e.target.value)}
              />
            )}
          />
          {errors.search && (
            <p className="text-red-500 text-sm">{errors.search.message}</p>
          )}
        </div>

        <div className="flex flex-col gap-1 min-w-[100px]">
          <Label htmlFor="status">Status</Label>
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <Select
                onValueChange={value => field.onChange(value || 'all')}
                value={field.value || 'all'}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {orderStatusOptions.map(option => (
                    <SelectItem
                      key={option.value}
                      value={option.value.toString()}
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="flex flex-col gap-1 min-w-[100px]">
          <Label htmlFor="order_side">Order Side</Label>
          <Controller
            name="order_side"
            control={control}
            render={({ field }) => (
              <Select
                onValueChange={value => field.onChange(value || 'all')}
                value={field.value || 'all'}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="buy">Buy</SelectItem>
                  <SelectItem value="sell">Sell</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="flex flex-col gap-1 min-w-[100px]">
          <Label htmlFor="source">Product Source</Label>
          <Controller
            name="source"
            control={control}
            render={({ field }) => (
              <Select
                onValueChange={value => field.onChange(value || 'all')}
                value={field.value || 'all'}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="1001">Fund</SelectItem>
                  <SelectItem value="1002">Stock</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="flex gap-1 self-end">
          <Button type="submit">Filter</Button>
          <Button type="button" variant="outline" onClick={handleReset}>
            Reset
          </Button>
        </div>
      </div>
    </form>
  );
};
